import { useCallback, useEffect, useMemo, useState } from "react";
import { hkAPI } from "../hkApi";
import { toast } from "sonner";
import CountUp from "@/pages/operations/CountUp";
import RoomCard from "./RoomCard";
import StaffCard from "./StaffCard";
import {
  Sparkles, RefreshCw, Users, Bed, Clock, ShieldCheck, Star, Radar,
  AlertTriangle, TrendingUp, Zap, Wrench, ChevronDown, X, User,
} from "lucide-react";

const REFRESH_MS = 30_000;

const STAFF_COLUMNS = [
  { key: "available",  label: "Available",  color: "#10b981" },
  { key: "cleaning",   label: "Cleaning",   color: "#f59e0b" },
  { key: "inspecting", label: "Inspecting", color: "#38bdf8" },
  { key: "break",      label: "On Break",   color: "#a78bfa" },
  { key: "off_duty",   label: "Off Duty",   color: "#64748b" },
];

export default function HKMissionControl() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [now, setNow] = useState(new Date());
  const [selectedFloor, setSelectedFloor] = useState(null); // floor_id or null
  const [drawerRoom, setDrawerRoom] = useState(null);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setReloading(true);
    try {
      const d = await hkAPI.missionControl();
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Mission Control");
    } finally { setLoading(false); setReloading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    const t = setInterval(() => fetchAll(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [fetchAll]);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  const rooms = data?.rooms || [];
  const floors = data?.floors || [];
  const staff = data?.staff_board || {};
  const kpis = data?.kpis || {};

  const filteredRooms = useMemo(
    () => (selectedFloor ? rooms.filter((r) => r.floor_id === selectedFloor) : rooms),
    [rooms, selectedFloor]
  );

  // Group rooms by floor for center grid
  const roomsByFloor = useMemo(() => {
    const map = new Map();
    for (const r of filteredRooms) {
      const key = r.floor_id || "unassigned";
      if (!map.has(key)) map.set(key, { floor_name: r.floor_name || "Unassigned", rooms: [] });
      map.get(key).rooms.push(r);
    }
    return Array.from(map.entries());
  }, [filteredRooms]);

  // Drag-and-drop reassignment
  const onDragStartStaff = (e, attendant) => {
    e.dataTransfer.setData("attendant_id", attendant.attendant_id);
    e.dataTransfer.setData("attendant_name", attendant.name || "");
    e.dataTransfer.effectAllowed = "move";
  };
  const onDropOnRoom = async (e, room) => {
    e.preventDefault();
    const attId = e.dataTransfer.getData("attendant_id");
    const attName = e.dataTransfer.getData("attendant_name");
    if (!attId || !room?.active_task?.id) {
      toast.error(room?.active_task ? "Missing attendant" : "This room has no active task to reassign to.");
      return;
    }
    try {
      await hkAPI.reassign(room.active_task.id, { assignee_user_id: attId });
      toast.success(`${attName} → Room ${room.room_number}`);
      fetchAll(true);
    } catch (err) {
      // Fall back to assign
      try {
        await hkAPI.assign(room.active_task.id, { assignee_user_id: attId });
        toast.success(`${attName} → Room ${room.room_number}`);
        fetchAll(true);
      } catch (err2) {
        toast.error(err2?.response?.data?.detail || "Reassignment failed");
      }
    }
  };

  return (
    <div className="on-dark min-h-full text-white relative overflow-hidden" data-testid="hk-mission-control"
      style={{ background: "#0B1020" }}>
      {/* Mesh gradient */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full opacity-[0.15] blur-3xl"
             style={{ background: "radial-gradient(circle, #f59e0b 0%, transparent 70%)" }}/>
        <div className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.13] blur-3xl"
             style={{ background: "radial-gradient(circle, #38bdf8 0%, transparent 70%)" }}/>
        <div className="absolute -bottom-32 left-1/3 w-[480px] h-[480px] rounded-full opacity-[0.13] blur-3xl"
             style={{ background: "radial-gradient(circle, #10b981 0%, transparent 70%)" }}/>
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,16,32,0.55) 0%, rgba(11,16,32,0.30) 30%, rgba(11,16,32,0.30) 70%, rgba(11,16,32,0.55) 100%)" }}/>
      </div>

      <div className="relative z-10 p-6 lg:p-7 space-y-5 max-w-[1800px] mx-auto">
        {/* ─── Header ─── */}
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-amber-300 mb-1.5">
              <Radar className="w-3.5 h-3.5"/> Housekeeping Mission Control
            </div>
            <h1 className="font-display text-[30px] sm:text-[36px] font-bold leading-none text-white">
              {data?.greeting || "Welcome"},{" "}
              <span className="bg-gradient-to-r from-amber-300 via-orange-300 to-fuchsia-300 bg-clip-text text-transparent">
                {data?.who || "Supervisor"}
              </span>
            </h1>
            <div className="mt-2 flex items-center gap-4 text-[12.5px] text-white/80">
              <span className="tabular-nums">{now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</span>
              <span className="w-1 h-1 rounded-full bg-white/40"/>
              <span className="tabular-nums" data-testid="hk-mc-clock">
                {now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
              </span>
              <span className="w-1 h-1 rounded-full bg-white/40"/>
              <span className="inline-flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${data ? "bg-emerald-400 animate-pulse" : "bg-white/40"}`}/>
                <span className="text-emerald-300">{data ? "Live · auto-refresh 30s" : "Warming up"}</span>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => fetchAll(true)} disabled={reloading} data-testid="hk-mc-refresh"
              className="h-10 w-10 rounded-xl bg-white/[0.08] hover:bg-white/[0.14] border border-white/[0.14] text-white inline-flex items-center justify-center disabled:opacity-50 transition-colors">
              <RefreshCw className={`w-4 h-4 ${reloading ? "animate-spin" : ""}`}/>
            </button>
          </div>
        </header>

        {/* ─── KPI Strip ─── */}
        <section className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-2" data-testid="hk-kpi-strip">
          <Kpi icon={ShieldCheck} accent="#10b981" label="Ready" value={<CountUp value={kpis.rooms_ready || 0}/>} sub="rooms"/>
          <Kpi icon={Bed}         accent="#ef4444" label="Dirty" value={<CountUp value={kpis.rooms_dirty || 0}/>} sub="pending"/>
          <Kpi icon={Sparkles}    accent="#f59e0b" label="Cleaning" value={<CountUp value={kpis.cleaning || 0}/>} sub="active"/>
          <Kpi icon={ShieldCheck} accent="#38bdf8" label="Inspection" value={<CountUp value={kpis.inspection || 0}/>} sub="waiting"/>
          <Kpi icon={Clock}       accent="#a78bfa" label="Avg time" value={<span><CountUp value={kpis.avg_cleaning_min || 0} decimals={1}/>m</span>} sub="per room"/>
          <Kpi icon={Star}        accent="#c084fc" label="VIP" value={<CountUp value={kpis.vip_pending || 0}/>} sub={`of ${kpis.vip_total || 0}`}/>
          <Kpi icon={Users}       accent="#22d3ee" label="Guests" value={<CountUp value={kpis.guest_waiting || 0}/>} sub="waiting"/>
          <Kpi icon={TrendingUp}  accent="#10b981" label="Productivity" value={<CountUp value={kpis.productivity_today || 0} decimals={1}/>} sub={`/ shift · ${kpis.rooms_done_today || 0} done`}/>
        </section>

        {/* ═══ 3-column Mission Control layout ═══ */}
        <section className="grid grid-cols-1 lg:grid-cols-[220px_1fr_340px] gap-5" data-testid="hk-mc-grid">
          {/* ── LEFT: Floor Navigator ── */}
          <aside className="rounded-3xl border border-white/[0.14] bg-[#0F1530]/70 backdrop-blur-md overflow-hidden max-h-[720px] flex flex-col" data-testid="hk-floor-navigator">
            <div className="px-4 py-3 border-b border-white/[0.10] flex items-center justify-between">
              <div>
                <h3 className="font-display text-[14px] font-semibold text-white">Floors</h3>
                <p className="text-[11px] text-white/70">{floors.length} · tap to filter</p>
              </div>
              {selectedFloor && (
                <button onClick={() => setSelectedFloor(null)} className="text-[11px] text-white/70 hover:text-white flex items-center gap-1"><X className="w-3 h-3"/> Clear</button>
              )}
            </div>
            <div className="overflow-y-auto flex-1 p-2 space-y-1.5">
              {floors.length === 0 && (
                <div className="text-center text-[12px] text-white/60 py-6">No floors configured yet.</div>
              )}
              {floors.map((f) => (
                <button
                  key={f.floor_id}
                  data-testid={`floor-${f.floor_id}`}
                  onClick={() => setSelectedFloor(selectedFloor === f.floor_id ? null : f.floor_id)}
                  className={`w-full text-left rounded-xl px-3 py-2.5 border transition-all ${
                    selectedFloor === f.floor_id
                      ? "bg-white/[0.12] border-white/25"
                      : "bg-white/[0.03] border-white/[0.10] hover:bg-white/[0.08]"}`}
                >
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="text-[13px] font-semibold text-white truncate">{f.floor_name}</span>
                    <span className="text-[10.5px] text-white/70 tabular-nums">{f.total}</span>
                  </div>
                  <div className="grid grid-cols-6 gap-0.5 h-2 rounded overflow-hidden">
                    <Bar count={f.ready} total={f.total} color="#10b981"/>
                    <Bar count={f.dirty} total={f.total} color="#ef4444"/>
                    <Bar count={f.cleaning} total={f.total} color="#f59e0b"/>
                    <Bar count={f.inspection} total={f.total} color="#38bdf8"/>
                    <Bar count={f.ooo} total={f.total} color="#6b7280"/>
                    <Bar count={f.vip} total={f.total} color="#c084fc"/>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 text-[10px] text-white/80">
                    <span className="text-emerald-300 tabular-nums">{f.ready} rdy</span>
                    <span className="text-red-300 tabular-nums">{f.dirty} dty</span>
                    <span className="text-amber-300 tabular-nums">{f.cleaning} cln</span>
                    {f.vip > 0 && <span className="text-fuchsia-300 tabular-nums ml-auto">{f.vip} VIP</span>}
                  </div>
                </button>
              ))}
            </div>
          </aside>

          {/* ── CENTER: Hotel Digital Twin ── */}
          <div className="rounded-3xl border border-white/[0.14] bg-[#0F1530]/70 backdrop-blur-md overflow-hidden flex flex-col max-h-[720px]" data-testid="hk-digital-twin">
            <div className="px-5 py-3 border-b border-white/[0.10] flex items-baseline justify-between">
              <div>
                <h2 className="font-display text-[15px] font-semibold text-white">Hotel Digital Twin</h2>
                <p className="text-[11.5px] text-white/70">Drag an available attendant onto any room with an active task to reassign.</p>
              </div>
              <span className="text-[11px] uppercase tracking-widest text-white/70 font-semibold">
                {filteredRooms.length} rooms
              </span>
            </div>
            <div className="overflow-y-auto flex-1 p-4 space-y-5">
              {loading ? (
                <div className="text-white/60 text-center py-10">Loading digital twin…</div>
              ) : roomsByFloor.length === 0 ? (
                <EmptyRooms/>
              ) : (
                roomsByFloor.map(([fid, group]) => (
                  <div key={fid}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-1 h-4 rounded-full bg-gradient-to-b from-amber-300 to-fuchsia-300"/>
                      <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-white/85">{group.floor_name}</div>
                      <div className="text-[11px] text-white/60 tabular-nums">· {group.rooms.length}</div>
                    </div>
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(92px,1fr))] gap-2">
                      {group.rooms.map((r) => (
                        <div key={r.room_id}
                          onDragOver={(e) => { if (r.active_task) e.preventDefault(); }}
                          onDrop={(e) => onDropOnRoom(e, r)}>
                          <RoomCard room={r} onClick={setDrawerRoom}/>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
            <RoomLegend/>
          </div>

          {/* ── RIGHT: Staff Board ── */}
          <aside className="rounded-3xl border border-white/[0.14] bg-[#0F1530]/70 backdrop-blur-md overflow-hidden max-h-[720px] flex flex-col" data-testid="hk-staff-board">
            <div className="px-4 py-3 border-b border-white/[0.10]">
              <h3 className="font-display text-[14px] font-semibold text-white">Staff Board</h3>
              <p className="text-[11.5px] text-white/70">
                Drag <span className="text-emerald-300 font-semibold">available</span> attendants to any room.
              </p>
            </div>
            <div className="overflow-y-auto flex-1 p-3 space-y-3">
              {STAFF_COLUMNS.map((col) => {
                const list = staff[col.key] || [];
                return (
                  <div key={col.key} data-testid={`staff-col-${col.key}`}>
                    <div className="flex items-center justify-between mb-2 px-1">
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: col.color }}/>
                        <span className="text-[10.5px] uppercase tracking-widest font-semibold text-white/85">{col.label}</span>
                      </div>
                      <span className="text-[11px] text-white/70 tabular-nums">{list.length}</span>
                    </div>
                    <div className="space-y-1.5">
                      {list.length === 0 ? (
                        <div className="text-[11px] text-white/50 italic px-1">—</div>
                      ) : list.map((a) => (
                        <StaffCard key={a.attendant_id} attendant={a} bucket={col.key} onDragStart={onDragStartStaff}/>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </aside>
        </section>

        {/* ─── AI Supervisor + Bottleneck + Events + Forecast + Heatmap ─── */}
        <section className="grid grid-cols-1 xl:grid-cols-[1.2fr_1fr_1fr] gap-5">
          <AIPanel bottlenecks={data?.bottlenecks || []} recommendations={data?.recommendations || []}/>
          <EventFeed events={data?.events || []}/>
          <div className="space-y-5">
            <ForecastCard f={data?.forecast || {}}/>
            <HeatmapCard heatmap={data?.heatmap || []}/>
          </div>
        </section>
      </div>

      {drawerRoom && (
        <RoomDrawer room={drawerRoom} onClose={() => setDrawerRoom(null)}/>
      )}
    </div>
  );
}

/* ───────────── Sub-components ───────────── */

function Kpi({ icon: Icon, accent, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-white/[0.14] bg-[#0F1530]/70 hover:bg-[#141C3D]/80 backdrop-blur-md transition-colors px-3.5 py-3 flex flex-col justify-between min-h-[96px]" data-testid={`hk-kpi-${label.toLowerCase().replace(/\s/g,"-")}`}>
      <div className="flex items-center justify-between text-[10.5px] uppercase tracking-widest text-white/80 font-semibold">
        <span>{label}</span>
        <Icon className="w-3.5 h-3.5" strokeWidth={2} style={{ color: accent }}/>
      </div>
      <div className="font-display font-bold text-white text-[22px] leading-none tabular-nums">{value}</div>
      <div className="text-[10.5px] text-white/60 truncate">{sub}</div>
    </div>
  );
}

function Bar({ count, total, color }) {
  const p = total ? Math.max(0, Math.min(100, (count / total) * 100)) : 0;
  return <div className="h-full" style={{ background: color, opacity: p > 0 ? 0.85 : 0.10 }}/>;
}

function RoomLegend() {
  const items = [
    ["Ready", "#10b981"], ["Dirty", "#ef4444"], ["Cleaning", "#f59e0b"],
    ["Inspection", "#38bdf8"], ["VIP", "#c084fc"], ["OOO", "#6b7280"],
  ];
  return (
    <div className="border-t border-white/[0.10] px-5 py-2.5 flex items-center gap-3.5 flex-wrap">
      {items.map(([label, c]) => (
        <div key={label} className="flex items-center gap-1.5 text-[10.5px] text-white/80 uppercase tracking-wider font-semibold">
          <span className="w-2.5 h-2.5 rounded" style={{ background: c }}/>{label}
        </div>
      ))}
    </div>
  );
}

function EmptyRooms() {
  return (
    <div className="text-center py-16">
      <Bed className="w-8 h-8 text-white/40 mx-auto mb-3"/>
      <div className="text-white/90 font-semibold">No rooms in scope.</div>
      <div className="text-[13px] text-white/70 mt-1">Configure rooms and floors in Master Data to populate the digital twin.</div>
    </div>
  );
}

function AIPanel({ bottlenecks, recommendations }) {
  const items = [
    ...bottlenecks.map((b) => ({ ...b, tag: "Bottleneck" })),
    ...recommendations.map((r) => ({ ...r, tag: "Recommendation" })),
  ];
  return (
    <div className="rounded-3xl border border-white/[0.14] bg-[#0F1530]/70 backdrop-blur-md overflow-hidden" data-testid="hk-ai-panel">
      <div className="px-5 py-4 border-b border-white/[0.10] flex items-baseline justify-between">
        <div>
          <h2 className="font-display text-[15px] font-semibold text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-300"/> AI Supervisor
          </h2>
          <p className="text-[11.5px] text-white/70">Deterministic · looks for imbalance, SLA breaches, VIP priority.</p>
        </div>
        <span className="text-[11px] uppercase tracking-widest text-white/70 font-semibold">{items.length}</span>
      </div>
      <div className="p-4 space-y-2 max-h-[380px] overflow-y-auto">
        {items.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-emerald-300 font-semibold">Ship-shape.</div>
            <div className="text-[12.5px] text-white/70 mt-1">Nothing to reallocate right now.</div>
          </div>
        ) : items.map((it, idx) => (
          <div key={idx} data-testid={`ai-item-${idx}`}
            className={`rounded-2xl p-3 border ${
              it.severity === "critical" ? "bg-red-500/15 border-red-500/40 shadow-[0_0_18px_rgba(244,63,94,0.20)]"
              : it.severity === "high" ? "bg-amber-500/15 border-amber-500/40"
              : "bg-white/[0.06] border-white/[0.14]"}`}>
            <div className="flex items-baseline justify-between mb-1">
              <div className="text-[13px] font-semibold text-white">{it.title}</div>
              <span className="text-[9.5px] uppercase tracking-widest text-white/70 font-semibold">{it.tag}</span>
            </div>
            <div className="text-[12px] text-white/85 leading-snug">{it.detail}</div>
            {it.action_hint && (
              <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-200">
                <Zap className="w-3 h-3"/> {it.action_hint}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function EventFeed({ events }) {
  return (
    <div className="rounded-3xl border border-white/[0.14] bg-[#0F1530]/70 backdrop-blur-md overflow-hidden" data-testid="hk-events">
      <div className="px-5 py-4 border-b border-white/[0.10] flex items-baseline justify-between">
        <div>
          <h2 className="font-display text-[15px] font-semibold text-white">Live Event Feed</h2>
          <p className="text-[11.5px] text-white/70">Room transitions · latest 25</p>
        </div>
      </div>
      <div className="p-4 max-h-[380px] overflow-y-auto space-y-1.5">
        {events.length === 0 ? (
          <div className="text-center text-[13px] text-white/70 py-8">No events yet.</div>
        ) : events.map((e, idx) => (
          <div key={idx} data-testid={`event-${idx}`}
            className="flex items-baseline gap-3 rounded-lg bg-white/[0.03] border border-white/[0.10] px-3 py-2 hover:bg-white/[0.06] transition-colors">
            <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
              style={{ background: e.state === "completed" ? "#10b981" : e.state === "in_progress" ? "#f59e0b" : e.state === "pending_inspection" ? "#38bdf8" : "#a78bfa" }}/>
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] text-white font-medium truncate">{e.title}</div>
              <div className="text-[10.5px] text-white/60 tabular-nums">{e.at?.slice(11, 16)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ForecastCard({ f }) {
  return (
    <div className="rounded-3xl border border-white/[0.14] bg-[#0F1530]/70 backdrop-blur-md overflow-hidden p-5" data-testid="hk-forecast">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-emerald-300"/>
        <h3 className="font-display text-[14px] font-semibold text-white">4-hour Forecast</h3>
      </div>
      <div className="grid grid-cols-2 gap-3 text-[12px]">
        <Stat label="Expected done" value={f.expected_completions || 0} tone="ok"/>
        <Stat label="Expected delays" value={f.expected_delays || 0} tone={f.expected_delays > 0 ? "warn" : "ok"}/>
        <Stat label="ETA to clear (min)" value={f.estimated_completion_time_min || 0}/>
        <Stat label="Avg cleaning (min)" value={f.avg_cleaning_min_used || 0}/>
      </div>
      {f.staff_shortage_forecast && (
        <div className="mt-3 flex items-center gap-2 text-[11.5px] rounded-lg bg-red-500/15 border border-red-500/40 text-red-100 px-3 py-2">
          <AlertTriangle className="w-3.5 h-3.5"/> Staff shortage projected — consider calling in support.
        </div>
      )}
    </div>
  );
}
function Stat({ label, value, tone }) {
  const color = tone === "warn" ? "text-amber-300" : tone === "ok" ? "text-emerald-300" : "text-white";
  return (
    <div className="rounded-lg bg-white/[0.04] border border-white/[0.10] px-3 py-2">
      <div className="text-[10.5px] uppercase tracking-wider text-white/70 font-semibold">{label}</div>
      <div className={`font-display font-bold text-[18px] ${color} tabular-nums`}><CountUp value={value || 0}/></div>
    </div>
  );
}

function HeatmapCard({ heatmap }) {
  return (
    <div className="rounded-3xl border border-white/[0.14] bg-[#0F1530]/70 backdrop-blur-md overflow-hidden p-5" data-testid="hk-heatmap">
      <div className="flex items-center gap-2 mb-3">
        <Radar className="w-4 h-4 text-fuchsia-300"/>
        <h3 className="font-display text-[14px] font-semibold text-white">Floor Heatmap</h3>
      </div>
      {heatmap.length === 0 ? (
        <div className="text-[12.5px] text-white/70">No floors configured.</div>
      ) : (
        <div className="space-y-2">
          {heatmap.map((h) => {
            const color = h.band === "red" ? "#f43f5e" : h.band === "amber" ? "#f59e0b" : "#10b981";
            const width = Math.min(100, Math.max(6, h.ratio * 50));
            return (
              <div key={h.floor_id} className="text-[12px]">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-white truncate">{h.floor_name}</span>
                  <span className="text-white/70 tabular-nums">{h.load}</span>
                </div>
                <div className="h-2 rounded bg-white/5 overflow-hidden">
                  <div className="h-full rounded transition-all duration-700" style={{ width: `${width}%`, background: color, boxShadow: `0 0 10px ${color}66` }}/>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RoomDrawer({ room, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="hk-room-drawer">
      <div className="absolute right-0 top-0 h-full w-[520px] max-w-full bg-[#0B1020] border-l border-white/[0.14] shadow-2xl flex flex-col on-dark" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-white/[0.10] flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-widest text-white/70 font-semibold mb-1">Room</div>
            <div className="font-display font-bold text-[36px] text-white leading-none tabular-nums">{room.room_number}</div>
            <div className="mt-2 text-[12.5px] text-white/80">{room.floor_name} · {room.room_type_id || "—"}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-white/70 hover:bg-white/10">
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <Row k="Status" v={room.current_status}/>
          <Row k="Housekeeping" v={room.housekeeping_status}/>
          <Row k="Operational" v={room.operational_status}/>
          <Row k="Next arrival" v={room.next_arrival_date || "—"}/>
          <Row k="VIP" v={room.is_vip ? "Yes" : "No"}/>
          <Row k="Guest" v={room.guest_name || "—"}/>
          {room.active_task && (
            <div className="rounded-2xl border border-white/[0.14] bg-white/[0.04] p-4 space-y-2">
              <div className="text-[11px] uppercase tracking-widest text-amber-200 font-semibold">Active Task</div>
              <Row k="State" v={room.active_task.state}/>
              <Row k="Type" v={room.active_task.task_type}/>
              <Row k="Priority" v={room.active_task.priority || "—"}/>
              <Row k="Attendant" v={room.active_task.attendant_name || "—"}/>
              {room.active_task.timer_min != null && <Row k="Timer" v={`${Math.round(room.active_task.timer_min)} min`}/>}
              {room.active_task.overdue && (
                <div className="flex items-center gap-2 text-[11.5px] text-red-200 bg-red-500/15 border border-red-500/40 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5"/> Past SLA
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline gap-4 border-b border-white/[0.08] pb-2 last:border-0">
      <div className="w-32 text-[11px] uppercase tracking-wider text-white/70 font-semibold">{k}</div>
      <div className="text-[13px] text-white font-medium truncate">{v ?? "—"}</div>
    </div>
  );
}
